"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { IconCalendarOff, IconChevronRight } from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { AppointmentQuickStatusMenu } from "@/components/appointments/appointment-quick-status-menu"
import { useAppointmentSheet } from "@/components/appointments/sheet/appointment-sheet-context"
import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { usePermissions } from "@/hooks/use-permissions"
import { useAuthStore } from "@/stores/auth-store"
import { formField } from "@/lib/appointment-presets"
import { cn } from "@/lib/utils"

import { getCalendarEvents } from "@/lib/calendar-events"
import {
  isVisibleAppointmentType,
  type AppointmentIntentType,
} from "@/components/calendar/calendar-modes"
import type { AppointmentStatus } from "@/types"
import type { CalendarEvent } from "@/components/calendar/calendar-types"
import { STATUS_BADGE_VARIANT, useAppointmentStatusLabel } from "@/lib/appointment-status"

interface TodayScheduleTimelineProps {
  /**
   * Restrict the feed to a single user's appointments (`?user_id=`). Omit for
   * the whole clinic. Doctors are force-scoped to themselves server-side
   * regardless of what is passed (AppointmentController::calendar, DOC-04).
   */
  userId?: number | string
  /**
   * Narrow the timeline to these appointment intents (client-side, null-typed
   * rows count as clinic_visit). Pass `presetAppointmentTypes(...)`'s result —
   * `null`/omitted shows everything. The coordinator dashboard uses this to
   * keep call-centre `lead_call` rows off the visits board.
   */
  types?: AppointmentIntentType[] | null
  /** Extra classes merged onto the panel. */
  className?: string
  /**
   * An outside refresh signal: bumping it reloads the timeline. The
   * coordinator dashboard bumps it when the desk board books an appointment.
   */
  refreshKey?: number
  /**
   * Called after an appointment changes from THIS panel (sheet or quick-status
   * menu) instead of the panel's own reload — for a caller that reloads every
   * sibling widget via `refreshKey`. Absent, the panel reloads itself.
   */
  onAppointmentsChanged?: () => void
}

/** Local `YYYY-MM-DDTHH:mm:ss` — no timezone suffix, matching the calendar page. */
function localStamp(date: Date, time: string): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}T${time}`
}

interface HourGroup {
  /** Hour bucket key, 0–23. */
  hour: number
  /** Localized hour label, e.g. "09:00". */
  label: string
  events: CalendarEvent[]
}

/**
 * Today's schedule as an hour-bucketed timeline — the shared "what's happening
 * now" rail for the doctor / coordinator / front-desk dashboards.
 *
 * Owns its own fetch (unlike the presentational chart widgets) because every
 * consumer wants the same today-window and the row shape never varies. The
 * fetch is gated internally on `appointments.view` AND on `usePermissions()`
 * `isLoaded`, so nothing fires during auth hydration and the panel simply
 * renders nothing for a user without the grant (hide-not-disable).
 *
 * Clicking any row navigates to /calendar.
 */
export function TodayScheduleTimeline({
  userId,
  types = null,
  className,
  refreshKey = 0,
  onAppointmentsChanged,
}: TodayScheduleTimelineProps) {
  const t = useTranslations("dashboard")
  const formatter = useFormatter()
  // 86-02: a row opens the shared appointment sheet (view mode).
  const appointmentSheet = useAppointmentSheet()
  const { isLoaded, can } = usePermissions()
  const categoryChipEnabled = useAuthStore(
    (s) => formField(s.user?.appointment_presets, "category") === "shown"
  )

  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const allowed = isLoaded && can("appointments.view")

  // Manual-refresh channel for the row context menu. Bumping the token re-runs
  // the SAME fetch effect below — the effect keeps its original shape (async
  // body + `cancelled` guard) rather than being hoisted into a callback, which
  // is what keeps it clear of react-hooks/set-state-in-effect.
  const [refreshToken, setRefreshToken] = useState(0)
  const reload = useCallback(() => setRefreshToken((n) => n + 1), [])
  // A caller that shares appointment changes across widgets reloads everyone
  // (this panel included, through `refreshKey`); alone, the panel reloads itself.
  const afterChange = onAppointmentsChanged ?? reload

  useEffect(() => {
    // Hold the fetch until permissions have resolved — firing and discarding
    // during hydration would 403-toast for legitimately ungranted users. When
    // the grant is absent the component renders nothing at all, so the stale
    // `isLoading` value is never observed (no setState needed here).
    if (!isLoaded || !allowed) return

    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const today = new Date()
        const response = await getCalendarEvents(
          localStamp(today, "00:00:00"),
          localStamp(today, "23:59:59"),
          userId === undefined ? undefined : String(userId),
        )
        const rows = (response.data ?? []).filter((event) =>
          isVisibleAppointmentType(event.type, types),
        )
        if (!cancelled) setEvents(rows)
      } catch {
        if (!cancelled) setEvents([])
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()

    return () => {
      cancelled = true
    }
    // `types` is keyed by content — callers memoize it, but a join() guard
    // costs nothing and survives a caller that rebuilds the array per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, allowed, userId, types?.join(","), refreshToken, refreshKey])

  const statusLabel = useAppointmentStatusLabel()

  const categoryLabel = useCallback(
    (category: string): string => {
      switch (category) {
        case "klea":
          return t("todaySchedule.category.klea")
        case "other":
          return t("todaySchedule.category.other")
        default:
          return category
      }
    },
    [t],
  )

  const groups = useMemo<HourGroup[]>(() => {
    const buckets = new Map<number, CalendarEvent[]>()
    for (const event of events) {
      const start = new Date(event.start)
      if (Number.isNaN(start.getTime())) continue
      const hour = start.getHours()
      const bucket = buckets.get(hour)
      if (bucket) bucket.push(event)
      else buckets.set(hour, [event])
    }

    return [...buckets.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([hour, list]) => {
        const anchor = new Date()
        anchor.setHours(hour, 0, 0, 0)
        return {
          hour,
          label: formatter.dateTime(anchor, { hour: "2-digit", minute: "2-digit" }),
          events: list.sort(
            (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
          ),
        }
      })
  }, [events, formatter])

  // Hide-not-disable: no grant (or still hydrating) → the widget does not exist.
  if (!allowed) return null

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("todaySchedule.title")} className={className}>
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-4 w-12 shrink-0" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-5 w-16 shrink-0 rounded-full" />
            </div>
          ))}
        </div>
      </ApolloPanel>
    )
  }

  if (groups.length === 0) {
    return (
      <ApolloPanel eyebrow={t("todaySchedule.title")} className={className}>
        <div className="flex h-[180px] flex-col items-center justify-center text-center">
          <div className="mb-3 rounded-full bg-muted p-3">
            <IconCalendarOff className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("todaySchedule.empty.title")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            {t("todaySchedule.empty.body")}
          </p>
        </div>
      </ApolloPanel>
    )
  }

  return (
    <ApolloPanel
      eyebrow={t("todaySchedule.title")}
      actions={
        <Badge variant="outline" className="text-[10px]">
          {t("todaySchedule.count", { count: events.length })}
        </Badge>
      }
      className={className}
    >
      <div className="scrollbar-thin max-h-[320px] space-y-4 overflow-auto">
        {groups.map((group) => (
          <div key={group.hour} className="flex gap-3">
            <div className="w-12 shrink-0 pt-1.5 text-[11px] font-medium tabular-nums text-text-dim">
              {group.label}
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              {group.events.map((event) => (
                <AppointmentQuickStatusMenu
                  key={event.id}
                  appointment={{
                    id: event.source.id,
                    status: event.status,
                    type: event.type,
                  }}
                  onChanged={afterChange}
                >
                  <button
                    type="button"
                    onClick={() => appointmentSheet.openAppointment(event.source.id, { onChanged: afterChange })}
                    className={cn(
                      "group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left",
                      "transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none",
                    )}
                  >
                    <span
                      className="h-6 w-[3px] shrink-0 rounded-full"
                      style={{ backgroundColor: event.color || "var(--chart-1)" }}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">
                        {event.lead?.full_name || event.title}
                      </span>
                      {event.lead?.full_name && event.title && (
                        <span className="block truncate text-[11px] text-text-dim">
                          {event.title}
                        </span>
                      )}
                    </span>
                    {categoryChipEnabled && event.category && (
                      <Badge variant="secondary" className="shrink-0 text-[10px]">
                        {categoryLabel(event.category)}
                      </Badge>
                    )}
                    <Badge
                      variant={STATUS_BADGE_VARIANT[event.status]}
                      className="shrink-0 text-[10px]"
                    >
                      {statusLabel(event.status)}
                    </Badge>
                    <IconChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                  </button>
                </AppointmentQuickStatusMenu>
              ))}
            </div>
          </div>
        ))}
      </div>
    </ApolloPanel>
  )
}
